import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from './ui/input';
import { StandardButton } from '../styles/StandardButtons';
import { supabase } from '../lib/supabase';
import { getApiUrl } from '../lib/api-config';
import { useUser } from '../contexts/UserContext';
import { colors } from '../design-system/colors';
import { AuthBanner } from './AuthBanner';
import '@/design-system/typography.css';
import '@/design-system/forms.css';
import './Setup.css';

type Step = 1 | 2;

// phase60: address/phone are no longer collected here at all -- the
// company is created with just a name (+ optional website), and the user
// is routed to Settings > Company right after onboarding to fill in the
// address (using the same AddressAutocomplete already used elsewhere in
// the app), which unlocks the rest of the app.
interface CompanyFormState {
  name: string;
  website: string;
}

interface UserFormState {
  firstName: string;
  lastName: string;
  phone: string;
}

function StepBadge({ step }: { step: 1 | 2 }) {
  return (
    <div
      className="font-small-button-white mb-4 inline-flex items-center gap-2 rounded-full px-4 py-1.5 uppercase tracking-wide"
      style={{ backgroundColor: colors.buttonSecondary }}
    >
      Step {step} of 2
    </div>
  );
}

export function OnboardingWizard() {
  const navigate = useNavigate();
  const { refreshUser } = useUser();

  const [checkingSession, setCheckingSession] = useState(true);
  const [email, setEmail] = useState<string>('');

  const [step, setStep] = useState<Step>(1);
  const [company, setCompany] = useState<CompanyFormState>({ name: '', website: '' });
  const [companyError, setCompanyError] = useState<string | null>(null);

  const [userInfo, setUserInfo] = useState<UserFormState>({ firstName: '', lastName: '', phone: '' });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Belt-and-suspenders against a double-click firing two overlapping
  // submits before React re-renders the disabled button -- the real
  // duplicate-prevention guarantee is the backend's own DB constraints
  // (see phase55_onboarding_rewrite.sql / phase60), this just avoids
  // sending two requests in the first place.
  const hasSubmittedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!session?.user) {
        navigate('/login', { replace: true });
        return;
      }

      // getSession() only reads the locally-cached token -- it doesn't
      // confirm the account still exists server-side. A stale session
      // (e.g. its account was deleted/deactivated) used to pass this
      // check and render the form anyway, only failing later -- and
      // confusingly -- on submit ("Invalid or expired session"). Found
      // live (2026-09-11). getUser() round-trips to Supabase Auth to
      // confirm the session is actually still good before showing the
      // form at all.
      const { data: { user }, error } = await supabase.auth.getUser();
      if (cancelled) return;
      if (error || !user) {
        await supabase.auth.signOut();
        navigate('/login', { replace: true });
        return;
      }

      setEmail(user.email || '');
      setCheckingSession(false);
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const handleCompanyNext = (e: React.FormEvent) => {
    e.preventDefault();
    setCompanyError(null);
    if (!company.name.trim()) {
      setCompanyError('Company name is required.');
      return;
    }
    setStep(2);
  };

  const handleBackToCompany = () => {
    setSubmitError(null);
    setStep(1);
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!userInfo.firstName.trim()) {
      setSubmitError('First name is required.');
      return;
    }
    if (!userInfo.lastName.trim()) {
      setSubmitError('Last name is required.');
      return;
    }

    if (hasSubmittedRef.current || submitting) return;
    hasSubmittedRef.current = true;
    setSubmitting(true);

    try {
      const response = await fetch(getApiUrl('api/onboarding/company'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: company.name.trim(),
          company_website: company.website.trim() || undefined,
          user_first_name: userInfo.firstName.trim(),
          user_last_name: userInfo.lastName.trim(),
          user_phone: userInfo.phone.trim() || undefined,
        }),
      });

      const result = await response.json().catch(() => null);

      if (!response.ok || !result?.success) {
        setSubmitError(result?.error || 'Failed to create your company. Please try again.');
        // Allow a real retry (e.g. a transient network error) -- only a
        // genuinely successful call should ever block further attempts.
        hasSubmittedRef.current = false;
        setSubmitting(false);
        return;
      }

      // Make companyId/role available app-wide before leaving this page.
      await refreshUser();
      // Company has no address yet -- App.tsx's route guard sends the user
      // to Settings > Company automatically to fill it in before they can
      // reach anywhere else, same mechanism as the /onboarding redirect.
      navigate('/', { replace: true });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create your company. Please try again.');
      hasSubmittedRef.current = false;
      setSubmitting(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="setup-container">
        {/* unmapped design value: 28rem also hardcoded in SignupPage.tsx/SignupSuccessPage.tsx's setup-content, no shared token exists for it */}
        <div className="setup-content" style={{ maxWidth: '28rem' }}>
          <AuthBanner />
          <p className="text-sm text-gray-600">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="setup-container">
      {/* unmapped design value: wider setup-content variant for step 2, no shared token exists for it */}
      <div className="setup-content" style={{ maxWidth: '36rem' }}>
        <AuthBanner />
        <StepBadge step={step === 1 ? 1 : 2} />
        <h1 className="h1">{step === 1 ? 'Tell us about your company' : 'Tell us about you'}</h1>

        {step === 1 && (
          <form onSubmit={handleCompanyNext}>
            {companyError && <div className="error-message" role="alert">{companyError}</div>}

            <div className="form-group">
              <label htmlFor="company-name" className="form-label">Company Name</label>
              <Input
                type="text"
                id="company-name"
                value={company.name}
                onChange={(e) => setCompany((c) => ({ ...c, name: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="company-website" className="form-label">Website (optional)</label>
              <Input
                type="url"
                id="company-website"
                placeholder="https://"
                value={company.website}
                onChange={(e) => setCompany((c) => ({ ...c, website: e.target.value }))}
              />
            </div>

            <div className="mt-4 flex flex-col items-end gap-2">
              <StandardButton customVariant="blue" type="submit">Next</StandardButton>
              <span className="font-dashboard-text-bold">Step 1 of 2 — next: your info</span>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleCreateUser}>
            {submitError && <div className="error-message" role="alert">{submitError}</div>}

            <div style={{ display: 'flex', gap: '1rem' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="user-first-name" className="form-label">First Name</label>
                <Input
                  type="text"
                  id="user-first-name"
                  value={userInfo.firstName}
                  onChange={(e) => setUserInfo((u) => ({ ...u, firstName: e.target.value }))}
                  required
                    autoComplete="given-name"
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="user-last-name" className="form-label">Last Name</label>
                <Input
                  type="text"
                  id="user-last-name"
                  value={userInfo.lastName}
                  onChange={(e) => setUserInfo((u) => ({ ...u, lastName: e.target.value }))}
                  required
                    autoComplete="family-name"
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="user-phone" className="form-label">Phone (optional)</label>
              <Input
                type="tel"
                id="user-phone"
                value={userInfo.phone}
                onChange={(e) => setUserInfo((u) => ({ ...u, phone: e.target.value }))}
                autoComplete="tel"
              />
            </div>
            <div className="form-group">
              <label htmlFor="user-email" className="form-label">Email</label>
              <Input
                type="email"
                id="user-email"
                value={email}
                disabled
                readOnly
              />
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <StandardButton customVariant="white" type="button" onClick={handleBackToCompany} disabled={submitting}>
                Back
              </StandardButton>
              <div className="flex flex-col items-end gap-2">
                <StandardButton customVariant="blue" type="submit" disabled={submitting}>
                  {submitting ? 'Creating…' : 'Create Account'}
                </StandardButton>
                <span className="font-dashboard-text-bold">Step 2 of 2 — last step</span>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
